import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { FaComment, FaLink, FaPhone, FaInfoCircle } from "react-icons/fa";
import PropTypes from "prop-types";
import SectionLabel from "../../widgets/SectionLabel";

const WabaButtonSelector = ({ onSelectionChange }) => {
  const [selected, setSelected] = useState([]);
  const [infoOpen, setInfoOpen] = useState(false);

  const items = [
    { id: "QUICK_REPLY", label: "Text", icon: <FaComment />, max: 2 },
    { id: "URL", label: "URL", icon: <FaLink />, max: 1 },
    { id: "PHONE_NUMBER", label: "Call", icon: <FaPhone />, max: 1 },
  ];

  const handleChipClick = (id) => {
    const currentCount = selected.filter((item) => item === id).length;
    const maxAllowed = items.find((item) => item.id === id)?.max || 1;

    // If already selected max times for this type → block
    if (currentCount >= maxAllowed) return;

    // If already 2 selected and not deselecting → block
    if (selected.length >= 2) return;

    // Add selection
    setSelected([...selected, id]);
  };

  const handleRemove = (id, index) => {
    // Remove by index (to handle duplicate QUICK_REPLY properly)
    const newSelected = [...selected];
    newSelected.splice(index, 1);
    setSelected(newSelected);
  };

  useEffect(() => {
    onSelectionChange(selected);
  }, [selected, onSelectionChange]);

  const getSelectionOrderText = () => {
    if (selected.length === 0) return "";
    return `Selection order: ${selected
      .map((id) => items.find((item) => item.id === id)?.label)
      .join(" → ")}`;
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 1, my: 2 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SectionLabel>Select Carousel Buttons</SectionLabel>
        <Tooltip title="View requirements">
          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            sx={{ p: 0.5 }}
          >
            <FaInfoCircle />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary">
        Select up to 2 options ({selected.length}/2 selected)
      </Typography>

      {/* Show available chips */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {items.map((item) => {
          const count = selected.filter((sel) => sel === item.id).length;
          const isDisabled =
            count >= item.max || (selected.length >= 2 && count === 0);

          return (
            <Chip
              key={item.id}
              icon={item.icon}
              label={item.label}
              onClick={() => handleChipClick(item.id)}
              color={count > 0 ? "primary" : "default"}
              variant="outlined"
              disabled={isDisabled}
              size="small"
              sx={{
                p: 1,
                cursor: isDisabled ? "not-allowed" : "pointer",
                fontWeight: count > 0 ? "bold" : "normal",
              }}
            />
          );
        })}
      </Box>

      {/* Show selected chips with remove option */}
      {selected.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {selected.map((id, idx) => (
            <Chip
              key={idx}
              icon={items.find((i) => i.id === id)?.icon}
              label={items.find((i) => i.id === id)?.label}
              onDelete={() => handleRemove(id, idx)}
              color="primary"
              size="small"
            />
          ))}
        </Box>
      )}

      {selected.length > 0 && (
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          {getSelectionOrderText()}
        </Typography>
      )}

      {selected.length === 2 && (
        <Alert
          severity="success"
          sx={{ py: 0.5, "& .MuiAlert-message": { padding: "4px 0" } }}
        >
          Maximum selection reached. Buttons will appear in the order shown
          above.
        </Alert>
      )}

      {/* Info Dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Carousel Button Requirements</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Important Requirements:</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Carousel buttons must be uniform (same type and order for all
            cards)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • Maximum of 2 buttons allowed per carousel card
          </Typography>
          <Typography variant="body2">
            • Selection order matters - buttons will appear in the order you
            select them
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

WabaButtonSelector.propTypes = {
  onSelectionChange: PropTypes.func.isRequired,
};

export default WabaButtonSelector;
